/**
 * Holds RUM data
 * @module
 */
var _ = require('optimizely-client-fn');

var actionTypes = require('../constants/action_types');

module.exports = {
  initialize: function() {
    this.__state = {
      inRumSample: false,
      id: null,
      src: null,
      RumHost: null,
      data: {
        extras: {},
      },
      apis: {},
      DOMObservation: {},
      featuresNeeded: {},
    };

    this.on(actionTypes.SET_RUM_DATA, this.__set);
    this.on(actionTypes.RECORD_API_USAGE, this.__updateAPIUsage);
    this.on(actionTypes.INITIALIZE_CHANGE_METRICS, this.__initializeChangeMetrics);
    this.on(actionTypes.RECORD_ACTIVATION_TYPE_USAGE, this.__updatePageActivationTypeUsage);
    this.on(actionTypes.RECORD_AUDIENCE_USAGE, this.__updateAudienceUsage);
    this.on(actionTypes.RECORD_CHANGE_MACROTASK_RATE, this.__recordChangeMacrotaskRate);
    this.on(actionTypes.RECORD_CHANGE_OVERHEATED, this.__recordChangeOverheated);
    this.on(actionTypes.RECORD_CHANGE_TYPE_USAGE, this.__updateChangeTypeUsage);
    this.on(actionTypes.RECORD_DOM_OBSERVATION_OCCURENCE, this.__recordDOMObservationOccurence);
    this.on(actionTypes.RECORD_INTEGRATION_USAGE, this.__updateIntegrationUsage);
    this.on(actionTypes.RECORD_LAYER_FEATURE_USAGE, this.__updateLayerFeatureUsage);
    this.on(actionTypes.RECORD_LAYER_POLICY_USAGE, this.__updateLayerPolicyUsage);
    this.on(actionTypes.RECORD_VIEW_FEATURE_USAGE, this.__updateViewFeatureUsage);
    this.on(actionTypes.RECORD_VIEWS_INITIALLY_ACTIVATED_COUNT, this.__updateViewsInitiallyActivatedCount);
    this.on(actionTypes.RECORD_VISITOR_ID_LOCATOR_USAGE, this.__updateVisitorIdLocatorUsage);
    this.on(actionTypes.RECORD_VISITOR_ID_ERROR, this.__recordVisitorIdError);
    this.on(actionTypes.RECORD_STICKY_BUCKETING_FEATURE, this.__updateStickyBucketingFeatureUsage);
  },

 /**
  * Set initial data for RUM sampling
  * @param {Object} payload
  * @param {boolean} [payload.inRumSample] Whether the visitor is included in the sample
  * @param {string} [payload.RumHost] Host for RUM requests
  * @param {string} [payload.id] unique Rum ID
  * @param {string} [payload.src] Value of client script's src attribute, if known
  * @param {RumData} [payload.data] Additional RUM data to include in payload
  * @private
  */
  __set: function(payload:any) {
    // Note(tyler): safe use of _.merge, all keys are defined in source code (ref: https://nvd.nist.gov/vuln/detail/CVE-2018-3721)
    _.merge(this.__state, payload);
    this.emitChange();
  },

  __updateAPIUsage: function(payload) {
    if (!this.__state.apis[payload.methodName]) {
      this.__state.apis[payload.methodName] = 0;
    }
    this.__state.apis[payload.methodName]++;
    this.emitChange();
  },

  /**
   * Initialize the RateMeter metrics to 0.
   *
   * This allows us to distinguish between page loads that have unincremented RateMeters (thanks to __OBSERVE_CHANGES_INDEFINITELY__)
   * and page loads that don't have RateMeters (i.e. __OBSERVE_CHANGES_INDEFINITELY__) at all.
   *
   * This also gives us numbers instead of undefined for *all* records that we may be collecting in our change rate
   * charts - cleaner and more consistent.
   *
   * @private
   */
  __initializeChangeMetrics: function() {
    // May have already been initialized or incremented by a previously-constructed change applier.
    if (_.isUndefined(this.__state.data.extras.changeMacrotaskRate)) {
      this.__state.data.extras.changeMacrotaskRate = 0;
    }
    // May have already been initialized or incremented by a previously-constructed change applier.
    if (_.isUndefined(this.__state.data.extras.numOverheatedChanges)) {
      this.__state.data.extras.numOverheatedChanges = 0;
    }
  },

  /**
   * Record that some change applier instance has (re)applied at a particular rate - or more precisely,
   * has re(applied) in a particular number of distinct macrotasks in a moving, snippet-wide time window.
   * @param {Object} payload
   * @param {number} payload.changeMacrotaskRate
   * @private
   */
  __recordChangeMacrotaskRate: function(payload:any) {
    if (_.isUndefined(this.__state.data.extras.changeMacrotaskRate)) {
      this.__state.data.extras.changeMacrotaskRate = 0;
    }
    if (payload.changeMacrotaskRate > this.__state.data.extras.changeMacrotaskRate) {
      this.__state.data.extras.changeMacrotaskRate = payload.changeMacrotaskRate;
    }
    this.emitChange();
  },

  /**
   * Record that a change applier instance has overheated
   * @private
   */
  __recordChangeOverheated: function() {
    if (_.isUndefined(this.__state.data.extras.numOverheatedChanges)) {
      this.__state.data.extras.numOverheatedChanges = 0;
    }
    this.__state.data.extras.numOverheatedChanges++;
    this.emitChange();
  },

  /**
   * Record an instance of a changeApplication counter.
   * @param payload
   * @private
   */
  __recordDOMObservationOccurence: function(payload) {
    if (!this.__state.DOMObservation[payload.counterName]) {
      this.__state.DOMObservation[payload.counterName] = 0;
    }
    this.__state.DOMObservation[payload.counterName]++;
    this.emitChange();
  },

  /**
   * Update features needed
   * @param {string} featureType
   * @param {Array.<string>} featuresUsed
   * @param {string} entityId
   * @private
   */
  __extendFeaturesNeeded: function(featureType:string, featuresUsed:string[], entityId:string) {
    if (_.isUndefined(this.__state.featuresNeeded[featureType])) {
      this.__state.featuresNeeded[featureType] = {} as any;
    }
    var featureTypeState = this.__state.featuresNeeded[featureType];
    _.each(featuresUsed, function(feature) {
      if (!featureTypeState[feature]) {
        featureTypeState[feature] = {} as any;
      }
      if (!featureTypeState[feature][entityId]) {
        featureTypeState[feature][entityId] = true;
      }
    });
  },

  /**
   * Update integration (third-party analytics) usage information
   * @param {Object} payload
   * @param {Array} payload.integrations
   * @param {string} payload.layerId
   */
  __updateIntegrationUsage: function(payload:any) {
    this.__extendFeaturesNeeded('integrations', payload.integrations, payload.layerId);
  },

  /**
   * Update the change type usage information
   * @param {Object} payload
   * @param {Array} payload.changeTypes
   * @param {string} payload.layerId
   */
  __updateChangeTypeUsage: function(payload:any) {
    this.__extendFeaturesNeeded('changeTypes', payload.changeTypes, payload.layerId);
  },

  /**
   * Update the page activation type info
   * @param {Object} payload
   * @param {string} payload.activationType One of enums.ViewActivationTypes
   * @param {string} payload.entityId  view/page id
   * @private
   */
  __updatePageActivationTypeUsage: function(payload:any) {
    this.__extendFeaturesNeeded('activationTypes', [payload.activationType], payload.entityId);
    this.emitChange();
  },

  /**
   * Update the view feature info
   * @param {Object} payload
   * @param {Array} payload.featuresUsed List of view features used to decide whether to activate view
   * @param {string} payload.entityId  view/page id
   * @private
   */
  __updateViewFeatureUsage: function(payload:any) {
    this.__extendFeaturesNeeded('viewFeatures', payload.featuresUsed, payload.entityId);
    this.emitChange();
  },

  /**
   * Update layer feature usage (currently just mutex)
   * Number of layers that were run that are part of a mutex group
   * @param {Object} payload
   * @param {string} payload.feature
   * @param {string} payload.entityId
   * @private
   */
  __updateLayerFeatureUsage: function(payload:any) {
    this.__extendFeaturesNeeded('layerFeatures', [payload.feature], payload.entityId);
    this.emitChange();
  },

  /**
   * Update layer policy usage
   * @param {Object} payload
   * @param {string} payload.policy
   * @param {string} payload.layerId
   * @private
   */
  __updateLayerPolicyUsage: function(payload:any) {
    this.__extendFeaturesNeeded('policy', [payload.policy], payload.layerId);
    this.emitChange();
  },

  /**
   * Update audiences needed to make decisions (includes audiences the visitor was not in)
   * @param {Object} payload
   * @param {Array} payload.audienceTypes
   * @param {string} payload.layerId
   * @private
   */
  __updateAudienceUsage: function(payload:any) {
    this.__extendFeaturesNeeded('audiences', payload.audienceTypes, payload.layerId);
    this.emitChange();
  },

  /**
   * Update the number of views the controller initially activated. This function is only
   * used in Edge SPA follow-on snippets and is only ever called once.
   *
   * This metric helps us estimate approximate how much flashing visitors to a SPA site running
   * Edge experience.
   * @param {Object} payload
   * @param {number} payload.viewsInitiallyActivatedCount
   * @private
   */
  __updateViewsInitiallyActivatedCount: function(payload:any) {
    this.__state.data.extras.viewsInitiallyActivatedCount = payload.viewsInitiallyActivatedCount;
    this.emitChange();
  },

  /**
   * Update customer-owned visitor id locator type usage information.
   * @param {Object} payload
   * @param {string} payload.visitorIdLocatorType
   * @private
   */
  __updateVisitorIdLocatorUsage: function(payload:any) {
    this.__extendFeaturesNeeded('visitorIdLocatorType', [payload.visitorIdLocatorType], payload.entityId);
    this.emitChange();
  },

  /**
   * Record all requests that expect a customer-owned visitor id. If the request throws an error, errorCustomVisitorId
   * is true.
   * @param {Object} payload
   * @param {boolean} payload.isError
   * @private
   */
  __recordVisitorIdError: function(payload:any) {
    this.__state.data.extras.errorCustomVisitorId = payload.isError;
    this.emitChange();
  },

  /**
   * Record that a given sticky bucketing "feature" is used by a given experiment ID
   *
   * @param {Object} payload
   * @param {string} payload.feature Either "preferredVariation", "stoppedVariation", or "preferredLayer"
   * @param {string} payload.id
   * @private
   */
  __updateStickyBucketingFeatureUsage: function(payload:any) {
    this.__extendFeaturesNeeded('stickyBucketing', [payload.feature], payload.id);
  },

 /**
  * Returns whether to sample RUM data or not
  * @return {boolean}
  */
  getSampleRum: function(): boolean {
    return this.__state.inRumSample;
  },

 /**
  * Returns RUM id
  * @return {string}
  */
  getRumId: function(): string {
    return this.__state.id;
  },

 /**
  * Returns RUM host
  * @return {string}
  */
  getRumHost: function(): string {
    return this.__state.RumHost;
  },

  getApiData: function() {
    return this.__state.apis;
  },

  getDOMObservationData: function() {
    return this.__state.DOMObservation;
  },

 /**
  * Returns initialized RUM data
  * @return {Object}
  */
  getRumData: function(): any {
    return _.cloneDeep(this.__state.data);
  },

 /**
  * Returns the script src url
  * @return {string}
  */
  getScriptSrc: function(): string {
    return this.__state.src;
  },

  /**
   * Returns formatted "features needed" data, counting entity ids associated with each feature
   * Data in store is like:
   * {
   *    activationTypes: {
   *      immediate: {
   *        123: true,
   *        456: true,
   *      },
   *      polling: {
   *        789: true,
   *      },
   *    },
   *    viewFeatures: {
   *      url: {
   *        999: true,
   *      },
   *    },
   * }
   * Returns data like:
   * {
   *    activationTypes: {
   *      immediate: 2,
   *      polling: 1,
   *    },
   *    viewFeatures: {
   *      url: 1,
   *    },
   * @return {Object}
   */
  getFeaturesNeededData: function(): any {
    var featuresNeeded = this.__state.featuresNeeded;
    var processedMap = {} as any;
    _.forOwn(featuresNeeded, function(featureSet, featureCategory) {
      var features = _.keys(featureSet);
      if (!_.isEmpty(features)) {
        processedMap[featureCategory] = {} as any;
      }
      _.forEach(features, function(feature) {
        processedMap[featureCategory][feature] = _.keys(featureSet[feature]).length;
      });
    });
    return processedMap;
  },
};
